import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl: string = error.config?.url ?? '';
      const isAuthEndpoint =
        requestUrl.includes('/api/v1/users/login') ||
        requestUrl.includes('/api/v1/users/register') ||
        requestUrl.includes('/api/v1/users/verify');

      if (!isAuthEndpoint) {
        localStorage.removeItem('token');
        window.dispatchEvent(new CustomEvent('authStateChanged'));
      }
    }
    return Promise.reject(error);
  }
);

export const submitFeedback = async (data: any, token?: string) => {
  return apiClient.post('/api/v1/feedback/submit', data, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

export const getFeedbackList = async (token?: string) => {
  return apiClient.get('/api/v1/feedback/list', {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

export const getEmotionLabels = async (languageCode: string) => {
  return apiClient.get(`/api/v1/feedback/emotion-labels/${languageCode}`);
};

export const submitEmotionVote = async (data: any, token?: string) => {
  return apiClient.post('/api/v1/feedback/emotion-vote', data, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

// Guest-specific functions
export const submitGuestFeedback = async (data: any, guestIdentifier: string) => {
  return apiClient.post('/api/v1/feedback/submit', {
    ...data,
    guest_identifier: guestIdentifier
  });
};

export const submitGuestEmotionVote = async (data: any, guestIdentifier: string) => {
  return apiClient.post('/api/v1/feedback/emotion-vote', {
    ...data,
    guest_identifier: guestIdentifier
  });
};

export const listApiKeys = async () => {
  return apiClient.get('/api/v1/api-keys/');
};

export const getApiKeyUsage = async (publicId: string) => {
  return apiClient.get(`/api/v1/api-keys/${publicId}/usage`);
};

export const createApiKey = async (tierId?: number) => {
  return apiClient.post('/api/v1/api-keys/', {
    tier_id: tierId ?? null,
  });
};

export const deleteApiKey = async (publicId: string) => {
  return apiClient.delete(`/api/v1/api-keys/${publicId}`);
};

export const createCheckoutSession = async (plan: 'starter' | 'pro' | 'scale') => {
  return apiClient.post('/api/v1/billing/create-checkout-session', { plan });
};

export const getCurrentUser = async () => {
  return apiClient.get('/api/v1/users/me');
};

export const forgotPassword = async (email: string) => {
  return apiClient.post('/api/v1/users/forgot-password', { email });
};

export const resetPassword = async (token: string, newPassword: string) => {
  return apiClient.post('/api/v1/users/reset-password', { token, new_password: newPassword });
};

export const createPortalSession = async () => {
  return apiClient.post('/api/v1/billing/create-portal-session');
};

export const getAdminDashboard = async () => {
  return apiClient.get('/api/v1/admin/dashboard');
};
